import { useEffect, useMemo, useState } from 'react';
import { fmtDate, daysUntil, dueLabel, waPhone } from '../api';
import Modal from '../components/Modal';
import BrandLogo from '../components/BrandLogo';
import {
  IconCash,
  IconChart,
  IconChat,
  IconCheck,
  IconClock,
  IconCopy,
  IconDatabase,
  IconDashboard,
  IconLogout,
  IconPlus,
  IconRefresh,
  IconSettings,
  IconUsers,
} from '../components/Icons';

const KEY = 'sc_admin_token';

async function adminApi(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem(KEY);
  const res = await fetch(`/api/admin${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) localStorage.removeItem(KEY);
    throw new Error(data.error || 'Error');
  }
  return data;
}

function Login({ onOk }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const d = await adminApi('/login', { method: 'POST', body: form });
      localStorage.setItem(KEY, d.token);
      onOk();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-auth-shell">
      <div className="admin-auth-brand"><BrandLogo caption="Control Center" admin /></div>
      <div className="admin-auth-card">
        <div className="eyebrow">Acceso administrativo</div>
        <h1>Gestiona tu red de vendedores</h1>
        <p>Planes, accesos y vencimientos en un solo lugar.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <label className="field-label">
            Correo
            <input className="input mt-2" type="email" placeholder="admin@tuempresa.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </label>
          <label className="field-label">
            Contraseña
            <input className="input mt-2" type="password" placeholder="Tu contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="btn-primary w-full" disabled={busy}>{busy ? 'Ingresando…' : 'Entrar al panel'}</button>
        </form>
      </div>
    </div>
  );
}

// Todos los accesos de vendedores viven bajo el mismo dominio de correo.
const EMAIL_DOMAIN = 'control.cr';
const APP_URL = 'controldestream.xyz';
const DEMO_HOURS = 48;

const emptyT = { name: '', emailUser: '', phone: '', password: '', passwordEdited: false, businessName: '', plan: 'mensual', productPlan: 'basic', demo: false, referralCode: '' };
const emptyFinance = { hosting: 0, mongodb: 0, domain: 0, whatsapp: 0, marketing: 0, other: 0 };
const PLAN_PRICES = { basic: 5000, pro: 10000, premium: 20000 };
const FINANCE_FIELDS = [
  { key: 'hosting', label: 'Servidor / hosting', detail: 'ICBServer2, aplicación y almacenamiento' },
  { key: 'mongodb', label: 'MongoDB Atlas', detail: 'Base de datos y respaldos' },
  { key: 'domain', label: 'Dominio y DNS', detail: 'Costo mensual equivalente' },
  { key: 'whatsapp', label: 'WhatsApp y mensajería', detail: 'Números, sesiones y servicios relacionados' },
  { key: 'marketing', label: 'Marketing', detail: 'Publicidad, diseño y adquisición' },
  { key: 'other', label: 'Otros gastos', detail: 'Soporte, herramientas y contingencias' },
];
const planLabel = (p) => (p === 'anual' ? 'Anual' : 'Mensual');
const productPlanLabel = (p) => (p === 'premium' ? 'Premium' : p === 'pro' ? 'Pro' : 'Básico');
const money = (value) => new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC', maximumFractionDigits: 0 }).format(Number(value) || 0);

function stateMeta(t) {
  if (!t.active) return { key: 'inactive', label: 'Inactivo', cls: 'status-neutral' };
  const expired = t.planExpires && new Date(t.planExpires) < new Date();
  if (expired) return { key: 'expired', label: t.status === 'demo' ? 'Demo vencido' : 'Vencido', cls: 'status-danger' };
  if (t.status === 'demo') return { key: 'demo', label: 'Demo', cls: 'status-warning' };
  return { key: 'active', label: 'Activo', cls: 'status-success' };
}

function whatsappInfo(phone) {
  const clean = String(phone || '').replace(/\D/g, '');
  if (!clean) return null;
  const full = clean.length === 8 ? `506${clean}` : clean;
  const local = clean.length === 8 ? `${clean.slice(0, 4)} ${clean.slice(4)}` : `+${clean}`;
  return { href: `https://wa.me/${full}`, copy: `+${full}`, label: clean.length === 8 ? `+506 ${local}` : local };
}

// Contraseña temporal sugerida: el nombre del negocio sin espacios + 123
const suggestedPassword = (businessName) => {
  const base = String(businessName || '').replace(/\s+/g, '');
  return base ? `${base}123` : '';
};

const fmtDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('es-CR', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

// Mensaje con los accesos, listo para enviar por WhatsApp. Sirve para el alta
// (`isNew`) y para reenviar los datos desde Gestionar. La contraseña solo se
// incluye cuando el panel la conoce: en la base únicamente vive su hash.
function accessMessage(tenant) {
  const vence = fmtDateTime(tenant.planExpires);
  const vigencia = tenant.demo
    ? `Tenés el modo demo activo por ${DEMO_HOURS} horas${vence ? `, hasta el ${vence}` : ''}.`
    : `Tu plan ${planLabel(tenant.plan).toLowerCase()} está activo${vence ? ` hasta el ${vence}` : ''}.`;
  return [
    tenant.isNew
      ? `¡Hola ${tenant.name}! 👋 Tu cuenta de StreamControl ya está lista.`
      : `¡Hola ${tenant.name}! 👋 Te comparto los accesos de tu cuenta en StreamControl.`,
    '',
    'Podés gestionarla desde una computadora, tablet o celular:',
    `https://${APP_URL}`,
    '',
    ...(tenant.businessName ? [`Negocio: ${tenant.businessName}`] : []),
    `Correo: ${tenant.email}`,
    ...(tenant.password
      ? [`Contraseña: ${tenant.password}`, '(Podés cambiarla cuando querás desde Ajustes.)']
      : ['Contraseña: la que ya venías usando. Si la perdiste, decime y te la reinicio.']),
    '',
    vigencia,
    '',
    'Si ya tenés tus cuentas en un Excel, decinos y te ayudamos a migrarlas al app automáticamente.',
    '',
    'La app está en desarrollo constante, así que cualquier opinión o idea de mejora nos sirve muchísimo. ¡Gracias! 🙌',
  ].join('\n');
}

// Copia al portapapeles con respaldo para navegadores sin permiso directo
async function copyText(value) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const helper = document.createElement('textarea');
      helper.value = value;
      helper.setAttribute('readonly', '');
      helper.style.position = 'fixed';
      helper.style.opacity = '0';
      document.body.appendChild(helper);
      helper.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(helper);
      return ok;
    } catch {
      return false;
    }
  }
}

// Resumen de accesos con el botón de envío por WhatsApp, compartido por el
// alta de tenants y el reenvío de datos desde Gestionar.
function AccessSharePanel({ tenant, intro, children }) {
  return (
    <div className="tenant-created-panel">
      {intro}
      <dl className="tenant-created-credentials">
        {tenant.businessName && <div><dt>Negocio</dt><dd>{tenant.businessName}</dd></div>}
        <div><dt>Correo</dt><dd>{tenant.email}</dd></div>
        <div><dt>Contraseña</dt><dd>{tenant.password || 'Se mantiene la actual'}</dd></div>
        <div><dt>Vence</dt><dd>{fmtDateTime(tenant.planExpires) || 'Sin fecha'}</dd></div>
      </dl>
      {tenant.phone ? (
        <a
          className="btn-primary w-full"
          href={`https://wa.me/${waPhone(tenant.phone)}?text=${encodeURIComponent(accessMessage(tenant))}`}
          target="_blank"
          rel="noreferrer"
        >
          <IconChat size={16} /> Enviar info por WhatsApp
        </a>
      ) : (
        <p className="form-error">Este tenant no tiene WhatsApp registrado, agrégalo para enviarle los accesos.</p>
      )}
      <div className="modal-actions">{children}</div>
    </div>
  );
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className={`admin-stat ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MetricCard({ icon, label, value, caption, tone = '' }) {
  return (
    <article className={`admin-kpi-card ${tone}`}>
      <div className="admin-kpi-label"><span>{icon}</span><small>{label}</small></div>
      <strong>{value}</strong>
      <p>{caption}</p>
    </article>
  );
}

function buildAnalytics(tenants) {
  const now = new Date();
  const paidTenants = tenants.filter((tenant) => stateMeta(tenant).key === 'active');
  const planStats = ['basic', 'pro', 'premium'].map((key) => {
    const count = paidTenants.filter((tenant) => (tenant.productPlan || 'basic') === key).length;
    return { key, label: productPlanLabel(key), price: PLAN_PRICES[key], count, revenue: count * PLAN_PRICES[key] };
  });
  const mrr = planStats.reduce((total, plan) => total + plan.revenue, 0);
  const monthBuckets = Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = new Intl.DateTimeFormat('es-CR', { month: 'short' }).format(date).replace('.', '');
    return { key, label, count: 0 };
  });
  tenants.forEach((tenant) => {
    if (!tenant.createdAt) return;
    const date = new Date(tenant.createdAt);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const bucket = monthBuckets.find((item) => item.key === key);
    if (bucket) bucket.count += 1;
  });
  const renewals = tenants
    .filter((tenant) => tenant.planExpires && tenant.active)
    .map((tenant) => ({ tenant, days: daysUntil(tenant.planExpires) }))
    .filter(({ days }) => days >= 0 && days <= 30)
    .sort((a, b) => a.days - b.days)
    .slice(0, 6);
  const recent = [...tenants]
    .filter((tenant) => tenant.createdAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  return {
    paidTenants,
    planStats,
    mrr,
    arr: mrr * 12,
    monthBuckets,
    renewals,
    recent,
    paidRate: tenants.length ? Math.round((paidTenants.length / tenants.length) * 100) : 0,
    whatsappCoverage: tenants.length ? Math.round((tenants.filter((tenant) => tenant.phone).length / tenants.length) * 100) : 0,
    referrals: tenants.filter((tenant) => tenant.referralBonusApplied).length,
  };
}

function AdminOverview({ analytics, onOpenTenants }) {
  const maxGrowth = Math.max(1, ...analytics.monthBuckets.map((item) => item.count));
  return (
    <div className="admin-tab-content">
      <section className="admin-kpi-grid">
        <MetricCard icon={<IconCash size={18} />} label="MRR estimado" value={money(analytics.mrr)} caption="Ingreso mensual de tenants activos" tone="is-mint" />
        <MetricCard icon={<IconUsers size={18} />} label="Base facturable" value={analytics.paidTenants.length} caption={`${analytics.paidRate}% del total de tenants`} />
        <MetricCard icon={<IconClock size={18} />} label="Renuevan en 30 días" value={analytics.renewals.length} caption="Seguimiento comercial próximo" tone="is-amber" />
        <MetricCard icon={<IconChart size={18} />} label="Proyección anual" value={money(analytics.arr)} caption="MRR actual multiplicado por 12" tone="is-violet" />
      </section>

      <section className="admin-analysis-grid">
        <article className="admin-insight-card admin-growth-panel">
          <div className="admin-card-heading">
            <div><span className="eyebrow">Crecimiento</span><h2>Altas de los últimos 6 meses</h2></div>
            <span className="admin-live-label"><i /> Datos actuales</span>
          </div>
          <div className="admin-growth-chart">
            {analytics.monthBuckets.map((item) => (
              <div key={item.key} className="admin-growth-column">
                <span>{item.count}</span>
                <div><i style={{ height: `${Math.max(7, (item.count / maxGrowth) * 100)}%` }} /></div>
                <small>{item.label}</small>
              </div>
            ))}
          </div>
        </article>

        <article className="admin-insight-card">
          <div className="admin-card-heading">
            <div><span className="eyebrow">Ingresos</span><h2>Distribución por plan</h2></div>
            <strong className="admin-card-total">{money(analytics.mrr)}</strong>
          </div>
          <div className="admin-plan-mix">
            {analytics.planStats.map((plan) => {
              const share = analytics.mrr ? Math.round((plan.revenue / analytics.mrr) * 100) : 0;
              return (
                <div key={plan.key}>
                  <span className={`product-plan-pill is-${plan.key}`}>{plan.label}</span>
                  <div><strong>{plan.count} tenants</strong><small>{money(plan.revenue)} · {share}%</small></div>
                  <i><em style={{ width: `${share}%` }} /></i>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="admin-analysis-grid is-secondary">
        <article className="admin-insight-card">
          <div className="admin-card-heading">
            <div><span className="eyebrow">Agenda</span><h2>Próximos vencimientos</h2></div>
            <button onClick={onOpenTenants}>Ver directorio →</button>
          </div>
          <div className="admin-compact-list">
            {analytics.renewals.map(({ tenant, days }) => (
              <div key={tenant.id}>
                <span className="tenant-avatar">{(tenant.businessName || tenant.name || 'SC').slice(0, 2).toUpperCase()}</span>
                <div><strong>{tenant.businessName || tenant.name}</strong><small>{productPlanLabel(tenant.productPlan)} · {fmtDate(tenant.planExpires)}</small></div>
                <b className={days <= 5 ? 'is-urgent' : ''}>{days === 0 ? 'Hoy' : `${days} ${days === 1 ? 'día' : 'días'}`}</b>
              </div>
            ))}
            {!analytics.renewals.length && <p className="admin-list-empty">No hay vencimientos durante los próximos 30 días.</p>}
          </div>
        </article>

        <article className="admin-insight-card">
          <div className="admin-card-heading">
            <div><span className="eyebrow">Adopción</span><h2>Salud de la plataforma</h2></div>
          </div>
          <div className="admin-health-grid">
            <div><strong>{analytics.whatsappCoverage}%</strong><span>con WhatsApp registrado</span></div>
            <div><strong>{analytics.referrals}</strong><span>altas por referidos</span></div>
          </div>
          <div className="admin-recent-list">
            <span>Altas recientes</span>
            {analytics.recent.map((tenant) => (
              <div key={tenant.id}><strong>{tenant.businessName || tenant.name}</strong><small>{fmtDate(tenant.createdAt)}</small></div>
            ))}
            {!analytics.recent.length && <p className="admin-list-empty">Aún no hay altas registradas.</p>}
          </div>
        </article>
      </section>
    </div>
  );
}

function AdminFinance({ analytics, finance, setFinance, updatedAt, saving, error, onSave }) {
  const infraTotal = FINANCE_FIELDS.reduce((total, field) => total + Number(finance[field.key] || 0), 0);
  const net = analytics.mrr - infraTotal;
  const margin = analytics.mrr ? Math.round((net / analytics.mrr) * 100) : 0;
  const breakEven = infraTotal ? Math.ceil(infraTotal / PLAN_PRICES.basic) : 0;
  const maxCost = Math.max(1, ...FINANCE_FIELDS.map((field) => Number(finance[field.key] || 0)));

  return (
    <div className="admin-tab-content">
      <section className="admin-kpi-grid">
        <MetricCard icon={<IconCash size={18} />} label="Ingreso mensual" value={money(analytics.mrr)} caption="Tenants activos, sin demos" tone="is-mint" />
        <MetricCard icon={<IconDatabase size={18} />} label="Infraestructura" value={money(infraTotal)} caption="Presupuesto operativo mensual" tone="is-amber" />
        <MetricCard icon={<IconChart size={18} />} label="Utilidad estimada" value={money(net)} caption="Ingreso menos gastos registrados" tone={net < 0 ? 'is-danger' : 'is-violet'} />
        <MetricCard icon={<IconDashboard size={18} />} label="Margen estimado" value={`${margin}%`} caption="Antes de impuestos y comisiones" />
      </section>

      <section className="admin-finance-layout">
        <form className="admin-budget-card" onSubmit={onSave}>
          <div className="admin-card-heading">
            <div><span className="eyebrow">Presupuesto mensual</span><h2>Gastos de operación</h2><p>Actualiza los costos reales para mantener una utilidad confiable.</p></div>
            {updatedAt && <small>Actualizado {fmtDate(updatedAt)}</small>}
          </div>
          <div className="admin-cost-fields">
            {FINANCE_FIELDS.map((field) => (
              <label key={field.key}>
                <span><strong>{field.label}</strong><small>{field.detail}</small></span>
                <div><b>₡</b><input type="number" min="0" step="100" value={finance[field.key]} onChange={(event) => setFinance({ ...finance, [field.key]: Number(event.target.value) })} /></div>
              </label>
            ))}
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="admin-budget-footer">
            <div><span>Total mensual</span><strong>{money(infraTotal)}</strong></div>
            <button className="btn-primary" disabled={saving}>{saving ? 'Guardando…' : 'Guardar presupuesto'}</button>
          </div>
        </form>

        <div className="admin-finance-side">
          <article className="admin-insight-card">
            <div className="admin-card-heading"><div><span className="eyebrow">Economía unitaria</span><h2>Ingresos por plan</h2></div></div>
            <div className="admin-unit-list">
              {analytics.planStats.map((plan) => (
                <div key={plan.key}>
                  <span className={`product-plan-pill is-${plan.key}`}>{plan.label}</span>
                  <p><strong>{plan.count} × {money(plan.price)}</strong><small>{money(plan.revenue)} / mes</small></p>
                </div>
              ))}
            </div>
            <div className="admin-break-even">
              <IconChart size={18} />
              <div><strong>{breakEven ? `${breakEven} tenants Básico` : 'Sin gastos registrados'}</strong><span>{breakEven ? 'cubrirían el presupuesto actual' : 'Añade tus costos para calcular el punto de equilibrio'}</span></div>
            </div>
          </article>

          <article className="admin-insight-card">
            <div className="admin-card-heading"><div><span className="eyebrow">Composición</span><h2>Distribución de gastos</h2></div></div>
            <div className="admin-expense-bars">
              {FINANCE_FIELDS.map((field) => {
                const value = Number(finance[field.key] || 0);
                return (
                  <div key={field.key}>
                    <span>{field.label}</span><strong>{money(value)}</strong>
                    <i><em style={{ width: `${(value / maxCost) * 100}%` }} /></i>
                  </div>
                );
              })}
            </div>
          </article>
        </div>
      </section>
      <p className="admin-finance-note">Los ingresos son una estimación mensual basada en el precio actual de cada plan. Los demos, tenants vencidos e inactivos no se contabilizan.</p>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(KEY));
  const [activeTab, setActiveTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finance, setFinance] = useState(emptyFinance);
  const [financeUpdatedAt, setFinanceUpdatedAt] = useState(null);
  const [savingFinance, setSavingFinance] = useState(false);
  const [financeError, setFinanceError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyT);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [editT, setEditT] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', password: '', plan: 'mensual', productPlan: 'basic', businessName: '' });
  const [editErr, setEditErr] = useState('');
  const [referralCheck, setReferralCheck] = useState({ status: 'idle', label: '' });
  const [createdTenant, setCreatedTenant] = useState(null);
  const [accessShare, setAccessShare] = useState(null);
  const [copiedPhone, setCopiedPhone] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [tenantData, financeData] = await Promise.all([
        adminApi('/tenants'),
        adminApi('/finance-settings'),
      ]);
      setTenants(tenantData);
      setFinance({ ...emptyFinance, ...(financeData.costs || {}) });
      setFinanceUpdatedAt(financeData.updatedAt || null);
    } catch {
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  useEffect(() => {
    const code = form.referralCode.trim();
    if (!creating || !code) {
      setReferralCheck({ status: 'idle', label: '' });
      return undefined;
    }
    if (!/^[A-Z]{3}\d{3}$/.test(code)) {
      setReferralCheck({ status: code.length === 6 ? 'invalid' : 'idle', label: code.length === 6 ? 'Usa 3 letras seguidas de 3 números.' : '' });
      return undefined;
    }

    let current = true;
    setReferralCheck({ status: 'checking', label: 'Validando código…' });
    const timer = window.setTimeout(() => {
      adminApi(`/referrals/${code}`)
        .then((result) => {
          if (!current) return;
          const owner = result.referrer?.businessName || result.referrer?.name || '';
          setReferralCheck(result.valid
            ? { status: 'valid', label: `Código válido de ${owner}. Se añadirá 1 mes gratis.` }
            : { status: 'invalid', label: 'No encontramos este código de referencia.' });
        })
        .catch(() => current && setReferralCheck({ status: 'invalid', label: 'No se pudo validar el código.' }));
    }, 320);

    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [creating, form.referralCode]);

  const counts = useMemo(() => {
    const result = { active: 0, demo: 0, expired: 0 };
    tenants.forEach((t) => {
      const key = stateMeta(t).key;
      if (key === 'active') result.active += 1;
      if (key === 'demo') result.demo += 1;
      if (key === 'expired' || key === 'inactive') result.expired += 1;
    });
    return result;
  }, [tenants]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      const state = stateMeta(t).key;
      if (filter !== 'all' && state !== filter && !(filter === 'expired' && state === 'inactive')) return false;
      if (!q) return true;
      return [t.name, t.businessName, t.email, t.phone].some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [tenants, query, filter]);

  const analytics = useMemo(() => buildAnalytics(tenants), [tenants]);

  if (!authed) return <Login onOk={() => setAuthed(true)} />;

  const notify = (text) => {
    setMsg(text);
    window.setTimeout(() => setMsg(''), 4200);
  };

  const saveFinance = async (event) => {
    event.preventDefault();
    setFinanceError('');
    setSavingFinance(true);
    try {
      const result = await adminApi('/finance-settings', { method: 'PUT', body: { costs: finance } });
      setFinance({ ...emptyFinance, ...(result.costs || {}) });
      setFinanceUpdatedAt(result.updatedAt || new Date().toISOString());
      notify('Presupuesto operativo actualizado.');
    } catch (err) {
      setFinanceError(err.message || 'No se pudo guardar el presupuesto.');
    } finally {
      setSavingFinance(false);
    }
  };

  const closeCreate = () => {
    setCreating(false);
    setCreatedTenant(null);
    setForm(emptyT);
    setError('');
    setReferralCheck({ status: 'idle', label: '' });
  };

  const create = async (e) => {
    e.preventDefault();
    setError('');
    const email = `${form.emailUser.trim()}@${EMAIL_DOMAIN}`;
    try {
      const created = await adminApi('/tenants', {
        method: 'POST',
        body: { ...form, email },
      });
      notify(created.referralBonusApplied
        ? `${form.businessName || form.name} fue creado con 1 mes gratis por referido.`
        : `${form.businessName || form.name} fue creado correctamente.`);
      // Se conserva la contraseña escrita para poder enviarla por WhatsApp:
      // el servidor solo devuelve su hash.
      setCreatedTenant({
        name: form.name,
        businessName: form.businessName,
        phone: form.phone,
        password: form.password,
        demo: form.demo,
        email: created.email || email,
        plan: created.plan || form.plan,
        planExpires: created.planExpires || null,
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyPhone = async (tenant, value) => {
    if (await copyText(value)) {
      setCopiedPhone(tenant.id);
      window.setTimeout(() => setCopiedPhone((current) => (current === tenant.id ? '' : current)), 2000);
    } else {
      notify('No se pudo copiar el número.');
    }
  };

  const runAction = async (t, path, body, success) => {
    try {
      await adminApi(`/tenants/${t.id}${path}`, { method: 'POST', body });
      notify(success);
      setEditT(null);
      load();
    } catch (err) {
      setEditErr(err.message);
    }
  };

  const toggle = async (t) => {
    try {
      await adminApi(`/tenants/${t.id}`, { method: 'PUT', body: { active: !t.active } });
      notify(`${t.businessName || t.email} fue ${t.active ? 'desactivado' : 'activado'}.`);
      setEditT(null);
      load();
    } catch (err) {
      setEditErr(err.message);
    }
  };

  const startDemo = (t) => {
    if (!confirm(`¿Activar prueba Demo de ${DEMO_HOURS} horas para ${t.businessName || t.email}?`)) return;
    runAction(t, '/demo', undefined, `Demo activado por ${DEMO_HOURS} horas.`);
  };

  const startPaid = (t) => {
    if (!confirm(`¿Iniciar el plan ${planLabel(t.plan).toLowerCase()} de ${t.businessName || t.email}?`)) return;
    runAction(t, '/start', { plan: t.plan }, 'Mensualidad iniciada correctamente.');
  };

  const renew = (t) => {
    if (!confirm(`¿Renovar a ${t.businessName || t.email} por un período ${planLabel(t.plan).toLowerCase()}?`)) return;
    runAction(t, '/renew', { plan: t.plan }, 'Plan renovado correctamente.');
  };

  const remove = async (t) => {
    if (!confirm(`¿Eliminar a ${t.businessName || t.email}? Se borrará su acceso.`)) return;
    try {
      await adminApi(`/tenants/${t.id}`, { method: 'DELETE' });
      notify('Tenant eliminado.');
      setEditT(null);
      load();
    } catch (err) {
      setEditErr(err.message);
    }
  };

  const openEdit = (t) => {
    setEditT(t);
    setAccessShare(null);
    setEditForm({
      name: t.name || '',
      email: t.email || '',
      phone: t.phone || '',
      password: '',
      plan: t.plan || 'mensual',
      productPlan: t.productPlan || 'basic',
      businessName: t.businessName || '',
    });
    setEditErr('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditErr('');
    try {
      const body = { name: editForm.name, email: editForm.email, phone: editForm.phone, plan: editForm.plan, productPlan: editForm.productPlan, businessName: editForm.businessName };
      if (editForm.password) body.password = editForm.password;
      await adminApi(`/tenants/${editT.id}`, { method: 'PUT', body });
      notify('Datos del tenant actualizados.');
      closeEdit();
      load();
    } catch (err) {
      setEditErr(err.message);
    }
  };

  // Guarda lo que haya en el formulario y arma el mensaje con los accesos.
  // Se guarda primero para no enviarle al vendedor una contraseña que todavía
  // no está activa.
  const shareAccess = async () => {
    setEditErr('');
    if (!String(editForm.phone || '').trim()) {
      setEditErr('Agregá el WhatsApp del tenant para poder enviarle los datos.');
      return;
    }
    try {
      const body = { name: editForm.name, email: editForm.email, phone: editForm.phone, plan: editForm.plan, productPlan: editForm.productPlan, businessName: editForm.businessName };
      if (editForm.password) body.password = editForm.password;
      const saved = await adminApi(`/tenants/${editT.id}`, { method: 'PUT', body });
      setAccessShare({
        name: saved.name,
        businessName: saved.businessName,
        email: saved.email,
        phone: saved.phone,
        password: editForm.password,
        demo: saved.status === 'demo',
        plan: saved.plan,
        planExpires: saved.planExpires,
      });
      load();
    } catch (err) {
      setEditErr(err.message);
    }
  };

  const closeEdit = () => {
    setEditT(null);
    setAccessShare(null);
    setEditErr('');
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setAuthed(false);
  };

  const countdown = (t) => {
    if (!t.planExpires) return <span className="text-slate-500">Sin fecha</span>;
    const d = daysUntil(t.planExpires);
    const color = d < 0 ? 'text-red-400' : d <= 5 ? 'text-amber-300' : 'text-slate-300';
    return (
      <div className={color}>
        <div className="font-medium">{fmtDate(t.planExpires)}</div>
        <div className="mt-0.5 text-[11px] opacity-75">{dueLabel(t.planExpires)}</div>
      </div>
    );
  };

  const tabMeta = {
    tenants: {
      eyebrow: 'Administración',
      title: 'Vendedores y accesos',
      subtitle: 'Supervisa planes, vencimientos y contacto sin perder de vista la operación.',
    },
    overview: {
      eyebrow: 'Inteligencia de negocio',
      title: 'Estado de StreamControl',
      subtitle: 'Una lectura rápida del crecimiento, los ingresos y los próximos movimientos.',
    },
    finance: {
      eyebrow: 'Rentabilidad',
      title: 'Finanzas e infraestructura',
      subtitle: 'Compara el ingreso recurrente con los costos reales de mantener la plataforma.',
    },
  };
  const currentTab = tabMeta[activeTab];

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <div className="admin-brand"><BrandLogo caption="Control Center" admin /></div>
        <button className="admin-logout" onClick={logout}><IconLogout size={16} /> Cerrar sesión</button>
      </header>

      <main className="admin-main">
        <nav className="admin-section-tabs" aria-label="Secciones administrativas">
          <button className={activeTab === 'tenants' ? 'is-active' : ''} onClick={() => setActiveTab('tenants')}>
            <IconUsers size={18} /><span>Tenants<small>Accesos y planes</small></span><b>{tenants.length}</b>
          </button>
          <button className={activeTab === 'overview' ? 'is-active' : ''} onClick={() => setActiveTab('overview')}>
            <IconChart size={18} /><span>Estadísticas<small>Crecimiento y salud</small></span>
          </button>
          <button className={activeTab === 'finance' ? 'is-active' : ''} onClick={() => setActiveTab('finance')}>
            <IconCash size={18} /><span>Finanzas<small>Ingresos e infraestructura</small></span>
          </button>
        </nav>

        <section className="admin-intro">
          <div>
            <div className="eyebrow">{currentTab.eyebrow}</div>
            <h1>{currentTab.title}</h1>
            <p>{currentTab.subtitle}</p>
          </div>
          {activeTab === 'tenants' && (
            <button className="btn-primary admin-create-button" onClick={() => { setError(''); setForm(emptyT); setCreatedTenant(null); setReferralCheck({ status: 'idle', label: '' }); setCreating(true); }}>
              <IconPlus size={17} /> Nuevo tenant
            </button>
          )}
        </section>

        {msg && <div className="success-toast"><IconCheck size={17} /> {msg}</div>}

        {activeTab === 'tenants' && (
          <div className="admin-tab-content">
        <section className="admin-stats-grid">
          <Stat label="Total de tenants" value={tenants.length} />
          <Stat label="Activos" value={counts.active} tone="is-success" />
          <Stat label="En demo" value={counts.demo} tone="is-warning" />
          <Stat label="Requieren atención" value={counts.expired} tone="is-danger" />
        </section>

        <section className="admin-directory">
          <div className="admin-directory-head">
            <div>
              <h2>Directorio de tenants</h2>
              <p>{shown.length} de {tenants.length} vendedores</p>
            </div>
            <div className="admin-tools">
              <div className="admin-search">
                <IconUsers size={17} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar negocio, correo o WhatsApp" />
              </div>
              <select className="admin-filter" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filtrar por estado">
                <option value="all">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="demo">En demo</option>
                <option value="expired">Requieren atención</option>
              </select>
            </div>
          </div>

          <div className="admin-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Negocio</th>
                  <th>Acceso</th>
                  <th>WhatsApp</th>
                  <th>Plan</th>
                  <th>Vencimiento</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => {
                  const st = stateMeta(t);
                  const wa = whatsappInfo(t.phone);
                  const initials = (t.businessName || t.name || t.email).slice(0, 2).toUpperCase();
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="tenant-identity">
                          <span className="tenant-avatar">{initials}</span>
                          <div>
                            <strong>{t.businessName || t.name}</strong>
                            <span>{t.name || 'Sin responsable'}</span>
                          </div>
                        </div>
                      </td>
                      <td><a className="tenant-email" href={`mailto:${t.email}`}>{t.email}</a></td>
                      <td>
                        {wa ? (
                          <span className="whatsapp-cell">
                            <a className="whatsapp-link" href={wa.href} target="_blank" rel="noreferrer">
                              <span className="whatsapp-dot" /> {wa.label}
                            </a>
                            <button
                              type="button"
                              className={`copy-chip ${copiedPhone === t.id ? 'is-copied' : ''}`}
                              onClick={() => copyPhone(t, wa.copy)}
                              title={copiedPhone === t.id ? 'Copiado' : 'Copiar WhatsApp'}
                              aria-label={copiedPhone === t.id ? 'WhatsApp copiado' : 'Copiar WhatsApp'}
                            >
                              {copiedPhone === t.id ? <IconCheck size={13} /> : <IconCopy size={13} />}
                            </button>
                          </span>
                        ) : <span className="missing-data">Sin WhatsApp</span>}
                      </td>
                      <td>
                        <div className="tenant-plan-stack">
                          <span className={`product-plan-pill is-${t.productPlan || 'basic'}`}>{productPlanLabel(t.productPlan)}</span>
                          <small>{planLabel(t.plan)}</small>
                        </div>
                      </td>
                      <td>{countdown(t)}</td>
                      <td><span className={`status-pill ${st.cls}`}><i /> {st.label}</span></td>
                      <td>
                        <div className="tenant-actions">
                          <button className="table-action-primary" onClick={() => renew(t)}><IconRefresh size={14} /> Renovar</button>
                          <button className="table-action" onClick={() => openEdit(t)}><IconSettings size={14} /> Gestionar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && shown.length === 0 && (
                  <tr><td colSpan="7"><div className="admin-empty">No encontramos tenants con esos filtros.</div></td></tr>
                )}
                {loading && (
                  <tr><td colSpan="7"><div className="admin-empty">Cargando directorio…</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
          </div>
        )}

        {activeTab === 'overview' && <AdminOverview analytics={analytics} onOpenTenants={() => setActiveTab('tenants')} />}
        {activeTab === 'finance' && (
          <AdminFinance
            analytics={analytics}
            finance={finance}
            setFinance={setFinance}
            updatedAt={financeUpdatedAt}
            saving={savingFinance}
            error={financeError}
            onSave={saveFinance}
          />
        )}
      </main>

      <Modal
        open={creating}
        title={createdTenant ? 'Tenant creado' : 'Crear nuevo tenant'}
        subtitle={createdTenant ? 'Comparte los accesos con el vendedor.' : 'Configura el acceso inicial del vendedor.'}
        dismissable={false}
        onClose={closeCreate}
      >
        {createdTenant ? (
          <AccessSharePanel
            tenant={{ ...createdTenant, isNew: true }}
            intro={(
              <p>
                <b>{createdTenant.businessName || createdTenant.name}</b> quedó listo
                {createdTenant.demo ? ` con demo de ${DEMO_HOURS} horas.` : '.'}
              </p>
            )}
          >
            <button type="button" className="btn-ghost" onClick={() => { setCreatedTenant(null); setForm(emptyT); setReferralCheck({ status: 'idle', label: '' }); }}>Crear otro</button>
            <button type="button" className="btn-primary" onClick={closeCreate}>Listo</button>
          </AccessSharePanel>
        ) : (
        <form onSubmit={create} className="form-grid">
          <label className="field-label">Nombre del responsable
            <input className="input mt-2" placeholder="Ej. Andrea López" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label className="field-label">Nombre del negocio
            <input
              className="input mt-2"
              placeholder="Ej. VisionPlay"
              value={form.businessName}
              onChange={(e) => setForm((current) => ({
                ...current,
                businessName: e.target.value,
                password: current.passwordEdited ? current.password : suggestedPassword(e.target.value),
              }))}
            />
          </label>
          <label className="field-label">Correo de acceso
            <div className="email-domain-input mt-2">
              <input
                placeholder="compufacil"
                value={form.emailUser}
                onChange={(e) => setForm({ ...form, emailUser: e.target.value.replace(/@.*$/, '').replace(/\s+/g, '') })}
                required
              />
              <span>@{EMAIL_DOMAIN}</span>
            </div>
          </label>
          <label className="field-label">WhatsApp
            <input className="input mt-2" placeholder="8888 1234" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <label className="field-label">Contraseña temporal
            <input
              className="input mt-2"
              placeholder="Mínimo 6 caracteres"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value, passwordEdited: true })}
              required
            />
            <span className="field-hint">Sugerida: nombre del negocio + 123.</span>
          </label>
          <label className="field-label">Plan inicial
            <select className="input mt-2" value={form.plan} disabled={form.demo} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option value="mensual">Mensual · 30 días</option>
              <option value="anual">Anual · 365 días</option>
            </select>
            {form.demo && <span className="field-hint">El demo vence en {DEMO_HOURS} horas.</span>}
          </label>
          <label className="field-label form-span">Versión de StreamControl
            <div className="admin-plan-selector mt-2">
              <button type="button" className={!form.demo && form.productPlan === 'basic' ? 'is-active' : ''} onClick={() => setForm({ ...form, productPlan: 'basic', demo: false })}>
                <span>Básico · ₡5.000</span><small>Gestión completa y WhatsApp asistido</small>
              </button>
              <button type="button" className={!form.demo && form.productPlan === 'pro' ? 'is-active is-pro' : ''} onClick={() => setForm({ ...form, productPlan: 'pro', demo: false })}>
                <span>Pro · ₡10.000</span><small>Operación, portal, cobros y automatizaciones</small>
              </button>
              <button type="button" className={!form.demo && form.productPlan === 'premium' ? 'is-active is-premium' : ''} onClick={() => setForm({ ...form, productPlan: 'premium', demo: false })}>
                <span>Premium · ₡20.000</span><small>Todo Pro + módulo de agente IA</small>
              </button>
              <button type="button" className={form.demo ? 'is-active is-demo' : ''} onClick={() => setForm({ ...form, productPlan: 'pro', demo: true })}>
                <span>Demo · {DEMO_HOURS} h</span><small>Prueba gratuita con todo el plan Pro</small>
              </button>
            </div>
          </label>
          <label className="field-label form-span">Código de referencia <span className="field-optional">Opcional</span>
            <input
              className="input referral-admin-input mt-2"
              placeholder="ABC123"
              maxLength="6"
              value={form.referralCode}
              onChange={(e) => setForm({ ...form, referralCode: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6) })}
            />
            <span className={`referral-validation is-${referralCheck.status}`}>
              {referralCheck.label || (form.demo
                ? '3 letras y 3 números. El mes gratis se aplica al pasar a un plan pagado.'
                : '3 letras y 3 números. Si es válido, suma 30 días gratis.')}
            </span>
          </label>
          {error && <p className="form-error form-span">{error}</p>}
          <div className="modal-actions form-span">
            <button type="button" className="btn-ghost" onClick={closeCreate}>Cancelar</button>
            <button className="btn-primary" disabled={!!form.referralCode && referralCheck.status !== 'valid'}>
              <IconPlus size={16} /> {form.demo ? 'Crear demo' : 'Crear tenant'}
            </button>
          </div>
        </form>
        )}
      </Modal>

      <Modal
        open={!!editT}
        title={editT?.businessName || editT?.name || 'Gestionar tenant'}
        subtitle={accessShare ? 'Cambios guardados. Comparte los accesos con el vendedor.' : 'Datos de acceso y ciclo de vida de la cuenta.'}
        size="lg"
        dismissable={false}
        onClose={closeEdit}
      >
        {editT && accessShare && (
          <AccessSharePanel
            tenant={accessShare}
            intro={(
              <p>
                Datos de <b>{accessShare.businessName || accessShare.name}</b> listos para enviar
                {accessShare.password ? ' con la contraseña nueva.' : '. La contraseña actual no se puede leer, así que el mensaje no la incluye.'}
              </p>
            )}
          >
            <button type="button" className="btn-ghost" onClick={() => setAccessShare(null)}>Volver</button>
            <button type="button" className="btn-primary" onClick={closeEdit}>Listo</button>
          </AccessSharePanel>
        )}
        {editT && !accessShare && (
          <div className="manage-tenant-layout">
            <form onSubmit={saveEdit} className="form-grid">
              <label className="field-label">Responsable
                <input className="input mt-2" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              </label>
              <label className="field-label">Negocio
                <input className="input mt-2" value={editForm.businessName} onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })} />
              </label>
              <label className="field-label">Correo de acceso
                <input className="input mt-2" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required />
              </label>
              <label className="field-label">WhatsApp
                <input className="input mt-2" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="8888 1234" />
              </label>
              <label className="field-label">Plan
                <select className="input mt-2" value={editForm.plan} onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}>
                  <option value="mensual">Mensual</option>
                  <option value="anual">Anual</option>
                </select>
              </label>
              <label className="field-label">Versión
                <select className="input mt-2" value={editForm.productPlan} onChange={(e) => setEditForm({ ...editForm, productPlan: e.target.value })}>
                  <option value="basic">Básico · WhatsApp Web</option>
                  <option value="pro">Pro · WhatsApp automático</option>
                  <option value="premium">Premium · Pro + agente IA</option>
                </select>
              </label>
              <label className="field-label">Nueva contraseña
                <input className="input mt-2" placeholder="Dejar vacía para conservarla" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                <span className="field-hint">Si la escribís, se incluye en el mensaje de WhatsApp.</span>
              </label>
              {editErr && <p className="form-error form-span">{editErr}</p>}
              <div className="modal-actions form-span">
                <button className="btn-primary">Guardar cambios</button>
              </div>
            </form>

            <aside className="tenant-lifecycle">
              <div className="tenant-lifecycle-summary">
                <span className={`status-pill ${stateMeta(editT).cls}`}><i /> {stateMeta(editT).label}</span>
                <dl>
                  <div><dt>Creado</dt><dd>{fmtDate(editT.createdAt)}</dd></div>
                  <div><dt>Vence</dt><dd>{editT.planExpires ? `${fmtDate(editT.planExpires)} · ${dueLabel(editT.planExpires)}` : 'Sin fecha'}</dd></div>
                  {editT.referralBonusApplied && <div><dt>Referido</dt><dd>{editT.referralCodeUsed} · 1 mes gratis</dd></div>}
                </dl>
              </div>
              <div className="tenant-lifecycle-actions">
                <div className="eyebrow">Acciones de cuenta</div>
                <button className="is-accent" onClick={shareAccess}>Enviar datos por WhatsApp</button>
                <button onClick={() => startDemo(editT)}>Activar demo de {DEMO_HOURS} horas</button>
                <button className="is-accent" onClick={() => startPaid(editT)}>Iniciar mensualidad pagada</button>
                <button onClick={() => renew(editT)}>Renovar plan {planLabel(editT.plan).toLowerCase()}</button>
                <button onClick={() => toggle(editT)}>{editT.active ? 'Desactivar acceso' : 'Activar acceso'}</button>
                <button className="is-danger" onClick={() => remove(editT)}>Eliminar tenant</button>
              </div>
            </aside>
          </div>
        )}
      </Modal>
    </div>
  );
}
